import React, { useEffect, useState, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import { api } from "../api/api";
import { useNavigate } from "react-router-dom"; // Importante para o header
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title
} from "chart.js";
import "./stylesheet/AdminReports.css";
import logoSvg from "../assets/logo.svg"; // Importe sua logo

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

export default function AdminReports() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("monthly");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState({
    bySector: [],
    ticketsByPeriod: [],
    eventsByPeriod: []
  });

  // Geração de cores dinâmicas para os gráficos ficarem coloridos
  const generateColors = (count) => {
    const palette = [
      'rgba(76, 102, 245, 0.7)', 'rgba(138, 43, 226, 0.7)', 
      'rgba(255, 99, 132, 0.7)', 'rgba(75, 192, 192, 0.7)', 
      'rgba(255, 159, 64, 0.7)', 'rgba(54, 162, 235, 0.7)'
    ];
    return Array.from({ length: count }, (_, i) => palette[i % palette.length]);
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/reports/getReport.php?period=${period}&month=${month}&year=${year}`
      );
      if (res.data?.success) {
        setData({
          bySector: res.data.bySector || [],
          ticketsByPeriod: res.data.ticketsByPeriod || [],
          eventsByPeriod: res.data.eventsByPeriod || []
        });
      }
    } catch (error) {
      console.error("Erro relatório:", error);
    } finally {
      setLoading(false);
    }
  }, [period, month, year]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false } // Ocultamos legenda para barras coloridas individuais
    },
    scales: {
      y: { beginAtZero: true, grid: { borderDash: [5, 5] } },
      x: { grid: { display: false } }
    }
  };

  const hasData = data.bySector.length > 0 || data.ticketsByPeriod.length > 0 || data.eventsByPeriod.length > 0;

  return (
    <div className="admin-reports-page">
      {/* HEADER PADRONIZADO */}
      <header className="portal-header">
        <div className="header-logo-group" onClick={() => navigate("/admin/dashboard")} style={{ cursor: "pointer" }}>
          <img src={logoSvg} alt="Logo" className="portal-logo-svg" />
        </div>
        <div className="header-nav-info">
          <button className="back-link-text" onClick={() => navigate("/admin/dashboard")}>
            Voltar ao Menu
          </button>
        </div>
      </header>

      <div className="reports-container">
        <div className="page-intro">
        </div>

        {/* FILTROS ESTILIZADOS */}
        <div className="filters-card">
          <div className="filter-group">
            <label>Frequência</label>
            <select value={period} onChange={e => setPeriod(e.target.value)}>
              <option value="daily">Diário</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensal</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Mês de Referência</label>
            <select value={month} onChange={e => setMonth(Number(e.target.value))}>
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("pt-BR", { month: "long" })}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Ano</label>
            <select value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>Processando dados...</p>
          </div>
        ) : !hasData ? (
          <div className="empty-state-card">
            <p>Nenhum dado encontrado para os filtros selecionados.</p>
          </div>
        ) : (
          <div className="reports-grid">
            {/* GRÁFICO SETORES */}
            {data.bySector.length > 0 && (
              <div className="report-card">
                <div className="card-info">
                   <h2>Chamados por Setor</h2>
                   <span className="total-badge">Total: {data.bySector.reduce((a,b) => a + Number(b.total), 0)}</span>
                </div>
                <div className="chart-container">
                  <Bar
                    options={chartOptions}
                    data={{
                      labels: data.bySector.map(i => i.sector),
                      datasets: [{
                        data: data.bySector.map(i => Number(i.total)),
                        backgroundColor: generateColors(data.bySector.length),
                        borderRadius: 5
                      }]
                    }}
                  />
                </div>
              </div>
            )}

            {/* GRÁFICO TICKETS PERÍODO */}
            {data.ticketsByPeriod.length > 0 && (
              <div className="report-card">
                <div className="card-info"><h2>Chamados por Período</h2></div>
                <div className="chart-container">
                  <Bar
                    options={chartOptions}
                    data={{
                      labels: data.ticketsByPeriod.map(i => i.label),
                      datasets: [{
                        label: "Chamados",
                        data: data.ticketsByPeriod.map(i => Number(i.total)),
                        backgroundColor: 'rgba(76, 102, 245, 0.6)',
                        borderColor: '#4c66f5',
                        borderWidth: 1,
                        borderRadius: 5
                      }]
                    }}
                  />
                </div>
              </div>
            )}

            {/* GRÁFICO EVENTOS PERÍODO */}
            {data.eventsByPeriod.length > 0 && (
              <div className="report-card">
                <div className="card-info"><h2>Eventos por Período</h2></div>
                <div className="chart-container">
                  <Bar
                    options={chartOptions}
                    data={{
                      labels: data.eventsByPeriod.map(i => i.label),
                      datasets: [{
                        label: "Eventos",
                        data: data.eventsByPeriod.map(i => Number(i.total)),
                        backgroundColor: 'rgba(138, 43, 226, 0.6)',
                        borderColor: '#8a2be2',
                        borderWidth: 1,
                        borderRadius: 5
                      }]
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}